<script lang="ts">
  import { onMount } from 'svelte';
  import ClueBuilder from './ClueBuilder.svelte';
  import { presentationStore, presentationClues } from '../../stores/presentationStore';
  import { clueBuilderStore } from '../../stores/clueBuilderStore';
  import { socketService } from '../../services/socketService';
  import { renderClue } from '../../lib/clueRenderer';
  import { gameStore } from '../../stores/gameStore';
  import { GamePhase } from '@blather/shared';

  $: presentation = $presentationStore.currentPresentation;
  $: clues = $presentationClues;
  $: gamePhase = $gameStore.gameState?.phase;

  let hasRequestedInitialFrameworks = false;

  // Request frameworks when phase becomes PRESENTING
  $: if (gamePhase === GamePhase.PRESENTING && !hasRequestedInitialFrameworks && presentation) {
    console.log('Phase is PRESENTING, requesting initial frameworks');
    hasRequestedInitialFrameworks = true;
    requestNewFrameworks();
  }

  /**
   * Announce to screen reader
   */
  function announceToScreenReader(message: string, priority: 'polite' | 'assertive' = 'polite') {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.style.position = 'absolute';
    announcement.style.left = '-10000px';
    announcement.style.width = '1px';
    announcement.style.height = '1px';
    announcement.style.overflow = 'hidden';
    announcement.textContent = message;
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  }

  function requestNewFrameworks() {
    clueBuilderStore.reset();
    clueBuilderStore.setLoading(true);
    socketService.emit('game:requestClueFrameworks');
  }

  function handleClueSubmitted() {
    announceToScreenReader('Clue submitted to guessers', 'assertive');
    // Immediately request new frameworks for next clue
    requestNewFrameworks();
  }

  onMount(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    socket.on('game:clueFrameworksAvailable', (data) => {
      clueBuilderStore.setFrameworksForClue(data);
    });

    socket.on('game:clueSubmitted', handleClueSubmitted);

    return () => {
      socket.off('game:clueFrameworksAvailable');
      socket.off('game:clueSubmitted');
      // Reset flag on unmount
      hasRequestedInitialFrameworks = false;
    };
  });
</script>

<div class="presenter-view">
  <div class="presenter-header">
    <h2>You're Presenting!</h2>
    {#if presentation}
      <div class="secret-word">
        <span class="label">Your Word:</span>
        <span class="word">{presentation.prompt.word}</span>
      </div>
    {/if}
  </div>

  <div class="presenter-content">
    <!-- Clue builder - always shown -->
    <section class="builder-section">
      <ClueBuilder mode="clue" onSubmit={handleClueSubmitted} />
    </section>

    <!-- Clues given -->
    <section class="clues-section">
      <h3>Clues Given:</h3>
      {#if presentation}
        <ul class="clues-list">
          <!-- Show opener first -->
          <li class="clue-item opener">
            <span class="clue-number">Opener</span>
            <span class="clue-text">{renderClue(presentation.opener)}</span>
          </li>
          <!-- Then show additional clues -->
          {#each clues as clue, index}
            <li class="clue-item">
              <span class="clue-number">#{index + 1}</span>
              <span class="clue-text">{renderClue(clue)}</span>
            </li>
          {/each}
        </ul>
      {/if}
    </section>

    <!-- Guesses received -->
    <section class="guesses-section">
      <h3>Guesses Received:</h3>
      {#if presentation}
        <div class="guess-stats">
          <p>{presentation.correctGuessers.length} player(s) guessed correctly</p>
        </div>

        {#if presentation.guesses.length > 0}
          <ul class="guesses-list">
            {#each presentation.guesses as guess}
              <li class="guess-item" class:correct={guess.isCorrect}>
                <span class="guesser-name">{guess.username}:</span>
                <span class="guess-text">"{guess.guessText}"</span>
                {#if guess.isCorrect}
                  <span class="correct-badge">✓ Correct</span>
                {/if}
              </li>
            {/each}
          </ul>
        {:else}
          <p class="empty-state">No guesses yet</p>
        {/if}
      {/if}
    </section>
  </div>
</div>

<style>
  .presenter-view {
    max-width: 1200px;
    margin: 0 auto;
    padding: 2rem;
  }

  .presenter-header {
    text-align: center;
    padding: 2rem;
    background: white;
    border-radius: 1rem;
    margin-bottom: 2rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  h2 {
    margin: 0 0 1rem 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
  }

  .secret-word {
    padding: 1rem;
    background: #fef3c7;
    border-radius: 0.5rem;
    display: inline-block;
  }

  .label {
    font-size: 0.875rem;
    color: #92400e;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    margin-right: 0.5rem;
  }

  .word {
    font-size: 1.5rem;
    font-weight: 700;
    color: #92400e;
  }

  .presenter-content {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  section {
    background: white;
    border-radius: 0.75rem;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  h3 {
    margin: 0 0 1rem 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1f2937;
  }

  .empty-state {
    color: #6b7280;
    text-align: center;
    padding: 1rem;
    margin: 0;
  }

  .clues-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .clue-item {
    padding: 0.75rem 1rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border-radius: 0.5rem;
    margin-bottom: 0.5rem;
    display: flex;
    gap: 1rem;
    font-size: 1.125rem;
  }

  .clue-item.opener {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    border: 2px solid #92400e;
  }

  .clue-number {
    font-weight: 700;
    min-width: 4rem;
  }

  .give-clue-button {
    width: 100%;
    padding: 1.25rem;
    font-size: 1.25rem;
    font-weight: 600;
    color: white;
    background: #3b82f6;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s;
  }

  .give-clue-button:hover {
    background: #2563eb;
    transform: translateY(-2px);
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }

  .give-clue-button:active {
    transform: translateY(0);
  }

  .guess-stats {
    padding: 0.75rem;
    background: #f3f4f6;
    border-radius: 0.5rem;
    margin-bottom: 1rem;
    text-align: center;
  }

  .guess-stats p {
    margin: 0;
    font-weight: 600;
    color: #1f2937;
  }

  .guesses-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .guess-item {
    padding: 0.75rem;
    background: #f9fafb;
    border-radius: 0.5rem;
    margin-bottom: 0.5rem;
    display: flex;
    gap: 0.5rem;
    align-items: center;
  }

  .guess-item.correct {
    background: #d1fae5;
    border: 2px solid #10b981;
  }

  .guesser-name {
    font-weight: 600;
    color: #1f2937;
  }

  .guess-text {
    flex: 1;
    color: #4b5563;
  }

  .correct-badge {
    color: #059669;
    font-weight: 700;
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .presenter-view {
      padding: 1rem;
    }

    h2 {
      font-size: 1.5rem;
    }

    .word {
      font-size: 1.25rem;
    }

    .clue-item {
      font-size: 1rem;
    }
  }
</style>
